/* global BigInt */

export const IDR_CASE_SCHEMA_VERSION = "idr-case.v1";

export const DOCUMENT_ROLES = [
  "TARGET_CLINICAL_RECORD",
  "TARGET_CLAIM_REMITTANCE",
  "PROVIDER_CREDENTIALS",
  "COMPARATOR_EOB",
  "COMPARATOR_AWARD_AGREEMENT",
  "PAYER_SUBMISSION",
  "BENCHMARK",
  "SECONDARY_NARRATIVE",
];

export const OFFER_BASES = [
  "TOTAL_REIMBURSEMENT",
  "PLAN_PAYMENT",
  "ADDITIONAL_PAYMENT_REQUESTED",
];

export const OUTPUT_MODES = [
  "pricing_statement",
  "clinical_support_letter",
  "combined_idr_submission",
];

export const EVIDENCE_CLASSIFICATIONS = [
  "ROUTINE",
  "ADDITIONAL",
  "UNCERTAIN",
];

export function createEmptyServiceLine(lineId = `line_${Date.now()}`) {
  return {
    lineId,
    cptHcpcs: "",
    modifiers: "",
    units: "",
    serviceScope: "",
    offerAmount: "",
    offerBasis: "",
    qpaAmount: "",
    qpaBasis: "",
    selectedPointRationale: "",
    rationaleSourceRef: null,
  };
}

export function getIDRServiceLines(idrCase) {
  const storedLines = Array.isArray(idrCase?.serviceLines) ? idrCase.serviceLines : [];
  const lines = storedLines.map((line) => ({
    ...createEmptyServiceLine(line?.lineId || ""),
    ...line,
    selectedPointRationale: typeof line?.selectedPointRationale === "string" ? line.selectedPointRationale : "",
    rationaleSourceRef: line?.rationaleSourceRef ?? null,
  }));
  if (
    Array.isArray(idrCase?.serviceLines) &&
    lines.length === 0 &&
    !String(idrCase?.metadata?.code || "").trim() &&
    !String(idrCase?.offer?.amount || "").trim()
  ) return [];
  const hasLineValues = lines.some((line) =>
    [line?.cptHcpcs, line?.offerAmount, line?.qpaAmount, line?.serviceScope]
      .some((value) => String(value || "").trim())
  );
  if (hasLineValues || (
    !String(idrCase?.metadata?.code || "").trim() &&
    !String(idrCase?.offer?.amount || "").trim()
  )) {
    return lines;
  }
  return [{
    ...createEmptyServiceLine("line_1"),
    cptHcpcs: idrCase.metadata?.code || "",
    modifiers: idrCase.metadata?.modifiers || "",
    units: idrCase.metadata?.units || "",
    serviceScope: idrCase.metadata?.serviceScope || "",
    offerAmount: idrCase.offer?.amount || "",
    offerBasis: idrCase.offer?.basis || "",
    qpaAmount: idrCase.payments?.qpa?.amount || "",
    qpaBasis: idrCase.payments?.qpa?.basis || "",
  }];
}

const MAX_MONEY_MINOR_UNITS = 999999999999999999n;

function absoluteBigInt(value) {
  return value < 0n ? -value : value;
}

function isSourceRef(value) {
  return value === null || (
    value &&
    typeof value === "object" &&
    typeof value.documentId === "string" &&
    typeof value.documentVersion === "number" &&
    (value.page === null || Number.isInteger(value.page))
  );
}

export function createEmptyIdrCase() {
  return {
    schemaVersion: IDR_CASE_SCHEMA_VERSION,
    intakeMode: "free_text",
    offerNarrative: "",
    metadata: {
      disputeId: "",
      recipientIdrEntity: "",
      targetIdentity: "",
      providerGroup: "",
      renderingProvider: "",
      signatureDetails: "",
      serviceDateStart: "",
      serviceDateEnd: "",
      location: "",
      code: "",
      modifiers: "",
      units: "",
      placeOfService: "",
      serviceScope: "",
      planName: "",
      planAdministrator: "",
      planType: "",
      planYear: "",
      initiationDate: "",
      jurisdiction: "",
      submissionDate: "",
    },
    outputMode: "combined_idr_submission",
    serviceLines: [createEmptyServiceLine("line_1")],
    offer: { amount: "", currency: "USD", basis: "", sourceRef: null },
    payments: {
      qpa: { amount: "", currency: "USD", basis: "", sourceRef: null },
      initialPayment: { amount: "", currency: "USD", basis: "", sourceRef: null },
      planLiability: { amount: "", currency: "USD", basis: "", sourceRef: null },
      patientResponsibility: { amount: "", currency: "USD", basis: "", sourceRef: null },
      serviceLineAllowance: { amount: "", currency: "USD", basis: "", sourceRef: null },
      supplementalPayment: { amount: "", currency: "USD", basis: "", sourceRef: null },
      paymentToPatient: { amount: "", currency: "USD", basis: "", sourceRef: null },
      paymentToProvider: { amount: "", currency: "USD", basis: "", sourceRef: null },
      reversal: { amount: "", currency: "USD", basis: "", sourceRef: null },
    },
    benchmark: {
      amount: "",
      currency: "USD",
      basis: "",
      provenance: "",
      sourceRef: null,
      status: "UNVERIFIED_CONTEXT_ONLY",
      permittedUse: "AUDIT_ONLY",
    },
    payerSubmissionStatus: "NOT_PROVIDED",
    payerArguments: "",
    research: { enabled: false, asOfDate: "" },
    clinicalFindings: [],
    comparators: [],
    legalSources: [],
    draftMarkdown: "",
    draftInputFingerprint: "",
    draftReviewNotes: [],
    reviewHistory: [],
  };
}

export function validateIdrCaseData(idrCase) {
  const errors = [];
  if (!idrCase || typeof idrCase !== "object" || Array.isArray(idrCase)) {
    return ["Case data must be an object."];
  }
  if (idrCase.schemaVersion !== IDR_CASE_SCHEMA_VERSION) {
    errors.push(`Unsupported case schema version: ${String(idrCase.schemaVersion)}.`);
  }
  if (!idrCase.metadata || typeof idrCase.metadata !== "object") {
    errors.push("Case metadata is missing.");
  } else {
    for (const key of Object.keys(createEmptyIdrCase().metadata)) {
      if (typeof idrCase.metadata[key] !== "string") {
        errors.push(`metadata.${key} must be a string.`);
      }
    }
  }
  if (idrCase.outputMode !== undefined && !OUTPUT_MODES.includes(idrCase.outputMode)) {
    errors.push("Output mode is invalid.");
  }
  if (idrCase.intakeMode !== undefined && !["free_text", "structured"].includes(idrCase.intakeMode)) {
    errors.push("Intake mode is invalid.");
  }
  if (idrCase.intakeMode === "free_text" &&
    (typeof idrCase.offerNarrative !== "string" || idrCase.offerNarrative.length > 10000)) {
    errors.push("IDR offer must be text of at most 10,000 characters.");
  }
  if (idrCase.serviceLines !== undefined && !Array.isArray(idrCase.serviceLines)) {
    errors.push("serviceLines must be an array.");
  }
  const lineIds = new Set();
  for (const line of Array.isArray(idrCase.serviceLines) ? idrCase.serviceLines : []) {
    if (!line || typeof line !== "object" || Array.isArray(line)) {
      errors.push("Each service line must be an object.");
      continue;
    }
    for (const key of ["lineId", "cptHcpcs", "modifiers", "units", "serviceScope", "offerAmount", "offerBasis", "qpaAmount", "qpaBasis", "selectedPointRationale"]) {
      if (typeof line[key] !== "string") errors.push(`Service line ${key} must be a string.`);
    }
    if (line.rationaleSourceRef !== null && line.rationaleSourceRef !== undefined && !isSourceRef(line.rationaleSourceRef)) {
      errors.push("Service line rationaleSourceRef is invalid.");
    }
    if (!line.lineId || lineIds.has(line.lineId)) errors.push("Service line IDs must be non-empty and unique.");
    lineIds.add(line.lineId);
    for (const key of ["offerAmount", "qpaAmount"]) {
      if (line[key] && parseMoneyToMinorUnits(line[key]) === null) {
        errors.push(`Service line ${key} must be an exact decimal with at most two fractional digits.`);
      }
    }
  }
  if (
    !idrCase.offer ||
    typeof idrCase.offer.amount !== "string" ||
    typeof idrCase.offer.currency !== "string" ||
    typeof idrCase.offer.basis !== "string"
  ) {
    errors.push("Offer amount, currency, and basis must be strings.");
  } else if (idrCase.offer.sourceRef !== undefined && !isSourceRef(idrCase.offer.sourceRef)) {
    errors.push("Offer sourceRef is invalid.");
  } else if (idrCase.offer.amount && parseMoneyToMinorUnits(idrCase.offer.amount) === null) {
    errors.push("Offer amount must be an exact decimal with at most two fractional digits.");
  }
  if (!idrCase.payments || typeof idrCase.payments !== "object") {
    errors.push("Payment inputs are missing.");
  } else {
    for (const key of Object.keys(createEmptyIdrCase().payments)) {
      const payment = idrCase.payments[key];
      if (
        !payment ||
        typeof payment.amount !== "string" ||
        typeof payment.currency !== "string" ||
        typeof payment.basis !== "string"
      ) {
        errors.push(`payments.${key} must contain string amount, currency, and basis.`);
      } else if (payment.sourceRef !== undefined && !isSourceRef(payment.sourceRef)) {
        errors.push(`payments.${key}.sourceRef is invalid.`);
      } else if (payment.amount && parseMoneyToMinorUnits(payment.amount) === null) {
        errors.push(`payments.${key}.amount must be an exact decimal with at most two fractional digits.`);
      }
    }
  }
  if (
    !idrCase.benchmark ||
    typeof idrCase.benchmark.amount !== "string" ||
    typeof idrCase.benchmark.currency !== "string" ||
    typeof idrCase.benchmark.basis !== "string" ||
    typeof idrCase.benchmark.provenance !== "string" ||
    (idrCase.benchmark.sourceRef !== undefined && !isSourceRef(idrCase.benchmark.sourceRef))
  ) {
    errors.push("Benchmark inputs are invalid.");
  } else if (idrCase.benchmark.amount && parseMoneyToMinorUnits(idrCase.benchmark.amount) === null) {
    errors.push("Benchmark amount must be an exact decimal with at most two fractional digits.");
  }
  if (!["PROVIDED", "NOT_PROVIDED", "UNKNOWN"].includes(idrCase.payerSubmissionStatus)) {
    errors.push("Payer submission status is invalid.");
  }
  for (const key of ["clinicalFindings", "comparators", "legalSources", "reviewHistory"]) {
    if (!Array.isArray(idrCase[key])) errors.push(`${key} must be an array.`);
  }
  if (typeof idrCase.payerArguments !== "string") errors.push("payerArguments must be a string.");
  if (
    !idrCase.research ||
    typeof idrCase.research.enabled !== "boolean" ||
    typeof idrCase.research.asOfDate !== "string"
  ) {
    errors.push("Research settings are invalid.");
  }
  if (typeof idrCase.draftMarkdown !== "string" || typeof idrCase.draftInputFingerprint !== "string") {
    errors.push("Draft fields are invalid.");
  }
  if (idrCase.draftReviewNotes !== undefined && !Array.isArray(idrCase.draftReviewNotes)) {
    errors.push("draftReviewNotes must be an array.");
  }
  for (const item of Array.isArray(idrCase.clinicalFindings) ? idrCase.clinicalFindings : []) {
    for (const key of ["factId", "finding", "action", "effect", "classification", "evidenceKind"]) {
      if (typeof item?.[key] !== "string") errors.push(`Clinical finding ${key} must be a string.`);
    }
    if (!EVIDENCE_CLASSIFICATIONS.includes(item?.classification)) {
      errors.push("Clinical finding classification is invalid.");
    }
    if (item?.sourceRef !== null && item?.sourceRef !== undefined && !isSourceRef(item.sourceRef)) {
      errors.push("Clinical finding sourceRef is invalid.");
    }
  }
  for (const item of Array.isArray(idrCase.comparators) ? idrCase.comparators : []) {
    if (
      typeof item?.comparatorId !== "string" ||
      typeof item?.amount !== "string" ||
      parseMoneyToMinorUnits(item?.amount) === null
    ) {
      errors.push("Comparator must have an ID and valid exact amount.");
    }
    if (item?.permittedUse !== "PENDING_REVIEW") {
      errors.push("Comparator permittedUse must remain PENDING_REVIEW in this workflow.");
    }
    if (item?.sourceRef !== null && item?.sourceRef !== undefined && !isSourceRef(item.sourceRef)) {
      errors.push("Comparator sourceRef is invalid.");
    }
    if (
      item?.serviceLineId !== undefined &&
      (typeof item.serviceLineId !== "string" ||
        !getIDRServiceLines(idrCase).some((line) => line.lineId === item.serviceLineId))
    ) {
      errors.push("Comparator serviceLineId must identify an existing service line.");
    }
  }
  return errors;
}

export function validateIdrManifest(manifest) {
  if (!Array.isArray(manifest)) return ["Source manifest must be an array."];
  const errors = [];
  const documentIds = new Set();
  for (const [index, document] of manifest.entries()) {
    const prefix = `Document ${index + 1}`;
    if (!document || typeof document !== "object") {
      errors.push(`${prefix} is invalid.`);
      continue;
    }
    if (typeof document.id !== "string" || !document.id) errors.push(`${prefix} has no document ID.`);
    if (documentIds.has(document.id)) errors.push(`${prefix} duplicates document ID ${document.id}.`);
    documentIds.add(document.id);
    if (!Number.isInteger(document.version) || document.version < 1) errors.push(`${prefix} has an invalid version.`);
    if (typeof document.name !== "string" || !document.name) errors.push(`${prefix} has no filename.`);
    if (!DOCUMENT_ROLES.includes(document.role)) errors.push(`${prefix} has an invalid role.`);
    if (typeof document.storageKey !== "string") errors.push(`${prefix} has an invalid storage key.`);
    if (typeof document.hash !== "string") errors.push(`${prefix} has an invalid source hash.`);
    if (
      document.pageCount !== null &&
      document.pageCount !== undefined &&
      (!Number.isInteger(Number(document.pageCount)) || Number(document.pageCount) < 1 || Number(document.pageCount) > 10000)
    ) {
      errors.push(`${prefix} has an invalid recorded page count.`);
    }
    if (!Array.isArray(document.pageAssignments)) {
      errors.push(`${prefix} pageAssignments must be an array.`);
      continue;
    }
    const pages = new Set();
    for (const assignment of document.pageAssignments) {
      if (!DOCUMENT_ROLES.includes(assignment?.role) || !Array.isArray(assignment?.pages)) {
        errors.push(`${prefix} has an invalid page-role assignment.`);
        continue;
      }
      for (const page of assignment.pages) {
        if (
          !Number.isInteger(page) ||
          page < 1 ||
          !Number.isInteger(Number(document.pageCount)) ||
          page > Number(document.pageCount)
        ) {
          errors.push(`${prefix} has an out-of-range page assignment.`);
        } else if (pages.has(page)) {
          errors.push(`${prefix} assigns page ${page} more than once.`);
        }
        pages.add(page);
      }
    }
  }
  return errors;
}

export function parseMoneyToMinorUnits(value) {
  const input = String(value ?? "").trim();
  if (!/^-?\d+(?:\.\d{1,2})?$/.test(input)) return null;

  const negative = input.startsWith("-");
  const unsigned = negative ? input.slice(1) : input;
  const [whole, fraction = ""] = unsigned.split(".");
  const minor = BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0"));
  if (absoluteBigInt(minor) > MAX_MONEY_MINOR_UNITS) return null;
  return negative ? -minor : minor;
}

export function formatMinorUnits(value, currency = "USD") {
  if (value === null || value === undefined) return null;
  const amount = BigInt(value);
  const absolute = amount < 0n ? -amount : amount;
  const sign = amount < 0n ? "-" : "";
  return `${sign}${currency} ${(absolute / 100n).toString()}.${(absolute % 100n)
    .toString()
    .padStart(2, "0")}`;
}

export function sumKnownMoney(values) {
  const parsed = values.map((value) => parseMoneyToMinorUnits(value));
  if (parsed.some((value) => value === null)) return null;
  return parsed.reduce((total, value) => total + value, 0n);
}

export function reconcilePayments(payments) {
  const values = [
    payments?.initialPayment?.amount,
    payments?.supplementalPayment?.amount,
    payments?.reversal?.amount,
  ];
  if (values.some((value) => String(value ?? "").trim() === "")) {
    return { status: "INCOMPLETE", calculatedMinorUnits: null, allowanceMinorUnits: null };
  }

  const calculatedMinorUnits = sumKnownMoney(values);
  const allowanceText = String(payments?.serviceLineAllowance?.amount ?? "").trim();
  const allowanceMinorUnits = allowanceText ? parseMoneyToMinorUnits(allowanceText) : null;

  if (calculatedMinorUnits === null || (allowanceText && allowanceMinorUnits === null)) {
    return { status: "INVALID_AMOUNT", calculatedMinorUnits: null, allowanceMinorUnits: null };
  }
  if (allowanceMinorUnits === null) {
    return { status: "CALCULATED", calculatedMinorUnits, allowanceMinorUnits: null };
  }

  return {
    status: calculatedMinorUnits === allowanceMinorUnits ? "MATCH" : "MISMATCH",
    calculatedMinorUnits,
    allowanceMinorUnits,
  };
}

export function compareOfferWithBenchmark(offer, benchmark) {
  const offerMinor = parseMoneyToMinorUnits(offer?.amount);
  const benchmarkMinor = parseMoneyToMinorUnits(benchmark?.amount);
  if (offerMinor === null || benchmarkMinor === null || benchmarkMinor <= 0n) return null;
  if (
    !offer?.basis ||
    offer.basis !== benchmark?.basis ||
    String(offer.currency || "") !== String(benchmark.currency || "")
  ) {
    return null;
  }

  const numerator = (benchmarkMinor - offerMinor) * 10000n;
  const roundedMagnitude =
    (absoluteBigInt(numerator) + benchmarkMinor / 2n) / benchmarkMinor;
  const roundedBasisPoints = numerator < 0n ? -roundedMagnitude : roundedMagnitude;
  const absolute = roundedBasisPoints < 0n ? -roundedBasisPoints : roundedBasisPoints;
  const sign = roundedBasisPoints < 0n ? "-" : "";
  return {
    method: "(benchmark - offer) / benchmark",
    roundedPercent: `${sign}${(absolute / 100n).toString()}.${(absolute % 100n)
      .toString()
      .padStart(2, "0")}%`,
    interpretation: "Historical comparison only; not an offer-increase recommendation.",
  };
}

function normalizedComparisonValue(value) {
  return String(value || "").trim().replace(/\s+/g, " ").toLocaleUpperCase("en-US");
}

export function screenComparatorEligibility(comparator, idrCase, manifest) {
  const serviceLines = getIDRServiceLines(idrCase);
  const comparatorCode = String(comparator?.codeAndModifiers || "").trim().split(/\s+/)[0];
  const serviceLine = serviceLines.find((line) => line.lineId === comparator?.serviceLineId) ||
    (serviceLines.length === 1
      ? serviceLines[0]
      : serviceLines.find((line) => line.cptHcpcs === comparatorCode));
  const source = (Array.isArray(manifest) ? manifest : []).find(
    (document) => document.id === comparator?.sourceRef?.documentId
  );
  const sourceValidation = validateSourceRef(comparator?.sourceRef, manifest);
  const sourceRefValid = sourceValidation.valid && sourceValidation.attached;
  const checks = [];
  const requiredFields = [
    ["planName", "Underlying plan"],
    ["administrator", "Payer / administrator"],
    ["physicianOrGroup", "Provider group"],
    ["codeAndModifiers", "Service code and modifiers"],
    ["serviceDates", "Service date(s)"],
    ["location", "Service location"],
    ["serviceScope", "Service scope"],
    ["amount", "Payment amount"],
    ["currency", "Currency"],
    ["basis", "Amount basis"],
    ["paymentBasis", "Payment type"],
  ];
  for (const [field, label] of requiredFields) {
    checks.push({
      code: `COMPARATOR_${field.toUpperCase()}_PRESENT`,
      status: String(comparator?.[field] || "").trim() ? "PRESENT" : "MISSING",
      detail: `${label} ${String(comparator?.[field] || "").trim() ? "is recorded" : "is missing"}.`,
    });
  }
  checks.push({
    code: "COMPARATOR_AMOUNT_VALID",
    status: parseMoneyToMinorUnits(comparator?.amount) === null ? "INVALID" : "VALID",
    detail:
      parseMoneyToMinorUnits(comparator?.amount) === null
        ? "Payment amount is not a valid exact decimal."
        : "Payment amount is a valid exact decimal.",
  });
  checks.push({
    code: "COMPARATOR_TARGET_SERVICE_LINE",
    status: serviceLine ? "PRESENT" : "MISSING",
    detail: serviceLine
      ? `Comparator is assessed against service line ${serviceLine.lineId} (${serviceLine.cptHcpcs}).`
      : "Select the exact target service line for this comparator.",
  });
  checks.push({
    code: "COMPARATOR_PRIMARY_SOURCE",
    status:
      sourceRefValid && ["COMPARATOR_EOB", "COMPARATOR_AWARD_AGREEMENT"].includes(source?.role)
        ? "PRESENT"
        : "MISSING_OR_WRONG_ROLE",
    detail:
      sourceRefValid && ["COMPARATOR_EOB", "COMPARATOR_AWARD_AGREEMENT"].includes(source?.role)
        ? "Source reference resolves to a comparator EOB or award/agreement."
        : "A valid comparator EOB or award/agreement source reference is required.",
  });

  const targetCode = [
    serviceLine?.cptHcpcs,
    serviceLine?.modifiers,
  ].filter(Boolean).join(" ");
  const targetDates = [
    idrCase?.metadata?.serviceDateStart,
    idrCase?.metadata?.serviceDateEnd,
  ].filter(Boolean).join(" to ");
  const comparisons = [
    ["PLAN_NAME", idrCase?.metadata?.planName, comparator?.planName, "Underlying plan"],
    ["ADMINISTRATOR", idrCase?.metadata?.planAdministrator, comparator?.administrator, "Payer / administrator"],
    ["PROVIDER_GROUP", idrCase?.metadata?.providerGroup, comparator?.physicianOrGroup, "Provider group"],
    ["CODE_SCOPE", targetCode, comparator?.codeAndModifiers, "Service code/modifiers"],
    ["SERVICE_DATES", targetDates, comparator?.serviceDates, "Service dates"],
    ["SERVICE_SCOPE", serviceLine?.serviceScope, comparator?.serviceScope, "Service scope"],
    ["LOCATION", idrCase?.metadata?.location, comparator?.location, "Service location"],
    ["CURRENCY", idrCase?.offer?.currency, comparator?.currency, "Currency"],
    ["AMOUNT_BASIS", serviceLine?.offerBasis || idrCase?.offer?.basis, comparator?.basis, "Amount basis"],
  ];
  for (const [code, target, candidate, label] of comparisons) {
    if (!String(target || "").trim() || !String(candidate || "").trim()) continue;
    const matches =
      normalizedComparisonValue(target) === normalizedComparisonValue(candidate);
    checks.push({
      code: `COMPARATOR_${code}_MATCH`,
      status: matches ? "MATCH" : "DIFFERENCE_REVIEW",
      detail: matches
        ? `${label} matches the target entry exactly.`
        : `${label} differs from the target entry and requires human assessment.`,
    });
  }

  const incomplete = checks.some((check) =>
    ["MISSING", "MISSING_OR_WRONG_ROLE", "INVALID"].includes(check.status)
  );
  const hasDifferences = checks.some((check) => check.status === "DIFFERENCE_REVIEW");
  return {
    status: incomplete
      ? "INCOMPLETE_NEEDS_REVIEW"
      : hasDifferences
        ? "DIFFERENCES_NEED_REVIEW"
        : "READY_FOR_HUMAN_ELIGIBILITY_REVIEW",
    permittedUse: "PENDING_HUMAN_LEGAL_REVIEW",
    checks,
    legalEligibilityDetermined: false,
  };
}

export function screenBenchmarkEligibility(benchmark, offer, manifest) {
  const source = (Array.isArray(manifest) ? manifest : []).find(
    (document) => document.id === benchmark?.sourceRef?.documentId
  );
  const sourceValidation = validateSourceRef(benchmark?.sourceRef, manifest);
  const sourceRefValid = sourceValidation.valid && sourceValidation.attached;
  const checks = [
    {
      code: "BENCHMARK_AMOUNT",
      status: parseMoneyToMinorUnits(benchmark?.amount) === null ? "MISSING_OR_INVALID" : "PRESENT",
      detail:
        parseMoneyToMinorUnits(benchmark?.amount) === null
          ? "An exact benchmark amount is required."
          : "An exact benchmark amount is recorded.",
    },
    {
      code: "BENCHMARK_PROVENANCE",
      status: String(benchmark?.provenance || "").trim() ? "PRESENT" : "MISSING",
      detail: String(benchmark?.provenance || "").trim()
        ? "Source methodology and limitations are recorded."
        : "Source methodology and limitations are missing.",
    },
    {
      code: "BENCHMARK_SOURCE",
      status: sourceRefValid && source?.role === "BENCHMARK" ? "PRESENT" : "MISSING_OR_WRONG_ROLE",
      detail:
        sourceRefValid && source?.role === "BENCHMARK"
          ? "Source reference resolves to a benchmark document."
          : "A valid benchmark-role source reference is required.",
    },
  ];
  for (const [code, left, right, label] of [
    ["CURRENCY", offer?.currency, benchmark?.currency, "Currency"],
    ["AMOUNT_BASIS", offer?.basis, benchmark?.basis, "Amount basis"],
  ]) {
    if (!String(left || "").trim() || !String(right || "").trim()) {
      checks.push({
        code: `BENCHMARK_${code}`,
        status: "MISSING",
        detail: `${label} is required for comparison.`,
      });
      continue;
    }
    checks.push({
      code: `BENCHMARK_${code}`,
      status:
        normalizedComparisonValue(left) === normalizedComparisonValue(right)
          ? "MATCH"
          : "DIFFERENCE_REVIEW",
      detail:
        normalizedComparisonValue(left) === normalizedComparisonValue(right)
          ? `${label} matches the offer.`
          : `${label} differs from the offer and requires human assessment.`,
    });
  }
  const incomplete = checks.some((check) =>
    ["MISSING", "MISSING_OR_INVALID", "MISSING_OR_WRONG_ROLE"].includes(check.status)
  );
  const hasDifferences = checks.some((check) => check.status === "DIFFERENCE_REVIEW");
  return {
    status: incomplete
      ? "INCOMPLETE_NEEDS_REVIEW"
      : hasDifferences
        ? "DIFFERENCES_NEED_REVIEW"
        : "READY_FOR_HUMAN_AUDIT_REVIEW",
    permittedUse: "AUDIT_ONLY",
    checks,
    valuationEligibilityDetermined: false,
  };
}

export function compareOfferToQpa(offer, qpa) {
  const offerMinor = parseMoneyToMinorUnits(offer?.amount);
  const qpaMinor = parseMoneyToMinorUnits(qpa?.amount);
  if (offerMinor === null || qpaMinor === null || qpaMinor <= 0n) return null;
  if (
    !offer?.basis ||
    offer.basis !== qpa?.basis ||
    String(offer.currency || "") !== String(qpa.currency || "")
  ) {
    return null;
  }

  const roundedHundredths = (offerMinor * 100n + qpaMinor / 2n) / qpaMinor;
  return {
    method: "offer amount / QPA amount",
    roundedMultiple: `${(roundedHundredths / 100n).toString()}.${(roundedHundredths % 100n)
      .toString()
      .padStart(2, "0")}x`,
  };
}

export function parsePageAssignments(text, pageCount) {
  const lines = String(text || "")
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean);
  const parsedCount = Number(pageCount);
  if (lines.length && (!Number.isInteger(parsedCount) || parsedCount < 1)) {
    return { assignments: [], errors: ["Enter the PDF page count before assigning page roles."] };
  }
  if (lines.length && parsedCount > 10000) {
    return { assignments: [], errors: ["Page-role assignment supports up to 10,000 pages per document."] };
  }

  const assignments = [];
  const errors = [];
  const assignedPages = new Set();

  lines.forEach((line, index) => {
    const match = line.match(/^(\d+)(?:-(\d+))?\s*=\s*([A-Z_]+)$/);
    if (!match) {
      errors.push(`Line ${index + 1}: use a range such as 1-2=TARGET_CLINICAL_RECORD.`);
      return;
    }
    const start = Number(match[1]);
    const end = Number(match[2] || match[1]);
    const role = match[3];
    if (!DOCUMENT_ROLES.includes(role)) {
      errors.push(`Line ${index + 1}: "${role}" is not a supported document role.`);
      return;
    }
    if (start < 1 || end < start || end > parsedCount) {
      errors.push(`Line ${index + 1}: page range must be within 1-${parsedCount}.`);
      return;
    }
    const pages = [];
    for (let page = start; page <= end; page += 1) {
      if (assignedPages.has(page)) {
        errors.push(`Line ${index + 1}: page ${page} is assigned more than once.`);
      } else {
        assignedPages.add(page);
        pages.push(page);
      }
    }
    if (pages.length) assignments.push({ role, pages });
  });

  return { assignments, errors };
}

export function validateSourceRef(sourceRef, manifest) {
  const document = (Array.isArray(manifest) ? manifest : []).find(
    (item) =>
      item.id === sourceRef?.documentId &&
      item.version === sourceRef?.documentVersion
  );
  if (!document) return { valid: false, reason: "Unknown document ID or version." };

  if (sourceRef?.page !== null && sourceRef?.page !== undefined) {
    const page = Number(sourceRef.page);
    if (
      !Number.isInteger(page) ||
      page < 1 ||
      !Number.isInteger(Number(document.pageCount)) ||
      page > Number(document.pageCount)
    ) {
      return { valid: false, reason: "Source page is missing or outside the recorded page count." };
    }
  }
  return {
    valid: true,
    attached: Boolean(document.storageKey),
    substantiveVerification: "NOT_VERIFIED",
  };
}

export function getBlockingIssues(idrCase) {
  if (idrCase?.intakeMode === "free_text") {
    return getPricingOfferIssues(idrCase).map((description) => ({ code: "OFFER_TEXT_REQUIRED", description }));
  }
  const issues = [];
  const metadata = idrCase?.metadata || {};
  const offerRequired = (idrCase?.outputMode || "pricing_statement") !== "clinical_support_letter";
  if (!String(metadata.targetIdentity || "").trim()) {
    issues.push({ code: "TARGET_IDENTITY_MISSING", description: "Target identity is required." });
  }
  const serviceLines = getIDRServiceLines(idrCase);
  if (!serviceLines.length) {
    issues.push({
      code: "SERVICE_SCOPE_MISSING",
      description: "Add at least one disputed service line.",
    });
  }
  for (const [index, line] of serviceLines.entries()) {
    const label = `Service line ${index + 1}${line.cptHcpcs ? ` (${line.cptHcpcs})` : ""}`;
    if (!String(line.cptHcpcs || "").trim() || !String(line.serviceScope || "").trim()) {
      issues.push({
        code: "SERVICE_SCOPE_MISSING",
        description: `${label}: enter a disputed code and service/package scope.`,
      });
    }
    const amountMinor = parseMoneyToMinorUnits(line.offerAmount);
    if (offerRequired && (amountMinor === null || amountMinor <= 0n)) {
      issues.push({
        code: "OFFER_AMOUNT_MISSING",
        description: `${label}: enter a valid positive exact offer amount.`,
      });
    }
    if (offerRequired && !OFFER_BASES.includes(line.offerBasis)) {
      issues.push({
        code: "OFFER_BASIS_MISSING",
        description: `${label}: select the offer's payment basis; it is never inferred.`,
      });
    }
    if (offerRequired) {
      const qpaMinor = parseMoneyToMinorUnits(line.qpaAmount);
      if (qpaMinor === null || qpaMinor <= 0n) {
        issues.push({
          code: "LINE_QPA_MISSING",
          description: `${label}: enter a valid positive exact QPA amount.`,
        });
      }
      if (!String(line.qpaBasis || "").trim()) {
        issues.push({
          code: "LINE_QPA_BASIS_MISSING",
          description: `${label}: enter the QPA amount basis; it is never inferred.`,
        });
      }
    }
  }
  return issues;
}

export function getPricingOfferIssues(idrCase) {
  if (idrCase?.intakeMode === "free_text") {
    return typeof idrCase.offerNarrative === "string" &&
      idrCase.offerNarrative.trim() && idrCase.offerNarrative.length <= 10000
      ? []
      : ["Describe your IDR offer before EOB pricing analysis (maximum 10,000 characters)."];
  }
  const lines = getIDRServiceLines(idrCase);
  if (!lines.length) return ["Add at least one disputed service line and its IDR offer."];
  return lines.flatMap((line, index) => {
    const amount = parseMoneyToMinorUnits(line.offerAmount);
    return amount === null || amount <= 0n || !OFFER_BASES.includes(line.offerBasis)
      ? [`Service line ${index + 1}: enter a positive IDR offer in dollars and select its offer basis before EOB pricing analysis.`]
      : [];
  });
}

export function buildFactOnlyDraft(idrCase, manifest, signatureName = "", pricingAnalysis = null, clinicalAnalysis = null) {
  const blockers = getBlockingIssues(idrCase);
  if (blockers.length) return { status: "NEEDS_INPUT", offerMarkdown: "", blockers };
  if ((pricingAnalysis?.codeDiscrepancies || []).length) {
    return {
      status: "NEEDS_INPUT",
      offerMarkdown: "",
      blockers: pricingAnalysis.codeDiscrepancies.map((item) => ({
        code: "REMITTANCE_CODE_DISCREPANCY",
        description: `Resolve entered code ${item.enteredCode} versus remittance code ${item.remittanceCode} for line ${item.lineId} before drafting.`,
      })),
    };
  }

  const metadata = idrCase.metadata;
  const freeTextIntake = idrCase.intakeMode === "free_text";
  const serviceLines = freeTextIntake
    ? (pricingAnalysis?.extractedServiceLines || []).map((line) => ({
        ...line, serviceScope: line.description, offerAmount: "", offerBasis: "",
      }))
    : getIDRServiceLines(idrCase);
  const outputMode = OUTPUT_MODES.includes(idrCase.outputMode)
    ? idrCase.outputMode
    : "pricing_statement";
  const includesClinical = ["clinical_support_letter", "combined_idr_submission"].includes(outputMode);
  const includesPricing = ["pricing_statement", "combined_idr_submission"].includes(outputMode);
  const findings = includesClinical ? (idrCase.clinicalFindings || []) : [];
  const clinicalReviewNotes = [];
  const findingLines = findings.flatMap((finding, index) => {
    const source = validateSourceRef(finding.sourceRef, manifest);
    const sourceDocument = manifest.find((item) => item.id === finding.sourceRef?.documentId);
    if (!source.valid || !source.attached || !sourceDocument) {
      clinicalReviewNotes.push(`Clinical work event ${index + 1} was omitted because its source is not attached and verifiable.`);
      return [];
    }
    const citation = `${sourceDocument.name}${finding.sourceRef.page ? `, p. ${finding.sourceRef.page}` : ""}`;
    return [`${index + 1}. ${finding.finding} The documented physician action was ${finding.action}, with this recorded effect on work: ${finding.effect}. Classification: ${finding.classification}. [${citation}]`];
  });
  const dateRange = [metadata.serviceDateStart, metadata.serviceDateEnd].filter(Boolean).join(" to ");
  const assessmentByLine = new Map(
    (pricingAnalysis?.serviceLineAssessments || []).map((assessment) => [assessment.lineId, assessment])
  );
  const offerLabel = {
    TOTAL_REIMBURSEMENT: "total reimbursement, including patient responsibility",
    PLAN_PAYMENT: "plan-payment amount",
    ADDITIONAL_PAYMENT_REQUESTED: "additional payment requested",
  };
  const rationaleReviewNotes = [];
  const lineOfferBlocks = includesPricing ? serviceLines.map((line, index) => {
    const amount = parseMoneyToMinorUnits(line.offerAmount);
    const assessment = assessmentByLine.get(line.lineId);
    const serviceDescription = [
      line.cptHcpcs,
      line.modifiers ? `modifier(s) ${line.modifiers}` : "",
      line.units ? `${line.units} unit(s)` : "",
      line.serviceScope,
    ].filter(Boolean).join("; ");
    const details = [
      `### Service line ${index + 1}: ${serviceDescription}`,
      ...(freeTextIntake ? [] : [`Provider offer: **${amount === null ? line.offerAmount : formatMinorUnits(amount, idrCase.offer?.currency || "USD")}** as ${offerLabel[line.offerBasis] || "the user-entered basis"}.`]),
    ];
    if (line.qpaAmount) {
      details.push(`${freeTextIntake ? "Extracted" : "Entered"} QPA: ${line.qpaAmount} ${line.currency || idrCase.offer?.currency || "USD"}; basis: ${line.qpaBasis || "not specified"}. The QPA is contextual and does not, by itself, explain or support the offer amount.`);
    } else {
      details.push(freeTextIntake
        ? "No explicitly labeled QPA could be extracted for this service line; verify the case EOB."
        : "No QPA amount is entered for this service line.");
    }
    if (assessment?.supportLevel === "NO_SAME_CODE_SUPPORT") {
      details.push("No same-code historical allowance support is recorded in the pricing assessment. The requested offer is preserved; no other procedure's payment is substituted and no valuation rationale is inferred.");
    } else if (assessment?.amountSupportExplanation?.trim()) {
      details.push(assessment.amountSupportExplanation.trim());
    } else {
      details.push(`${pricingAnalysis ? "No same-code historical allowance support is recorded in the pricing assessment." : "No same-code comparator support is entered in the case data."} The requested offer is preserved; no other procedure's payment is substituted and no unsupported valuation rationale is stated.`);
    }
    if (assessment?.qpaComparisonExplanation?.trim()) {
      details.push(`QPA context (separate from offer-amount support): ${assessment.qpaComparisonExplanation.trim()}`);
    }
    if (
      assessment?.supportLevel !== "NO_SAME_CODE_SUPPORT" &&
      line.selectedPointRationale?.trim()
    ) {
      const source = validateSourceRef(line.rationaleSourceRef, manifest);
      const sourceDocument = manifest.find((item) => item.id === line.rationaleSourceRef?.documentId);
      if (source.valid && source.attached && sourceDocument) {
        const citation = `${sourceDocument.name}${line.rationaleSourceRef.page ? `, p. ${line.rationaleSourceRef.page}` : ""}`;
        details.push(`Supported distinction for the selected offer point: ${line.selectedPointRationale.trim()} [${citation}]`);
      } else {
        rationaleReviewNotes.push(`The selected-point rationale for ${line.cptHcpcs} was not included because its source is not attached and verifiable.`);
      }
    }
    const citations = [...(line.sourceRefs || []), ...(assessment?.sourceRefs || [])].map((sourceRef) => {
      const source = validateSourceRef(sourceRef, manifest);
      const document = manifest.find((item) => item.id === sourceRef.documentId);
      return source.valid && document
        ? `${document.name}${sourceRef.page ? `, p. ${sourceRef.page}` : ""}`
        : "";
    }).filter(Boolean);
    if (citations.length) details.push(`Pricing sources: ${citations.join("; ")}.`);
    return details.join("\n\n");
  }) : [];
  const allOffers = serviceLines.map((line) => parseMoneyToMinorUnits(line.offerAmount));
  const referenceTotal = allOffers.every((amount) => amount !== null)
    ? allOffers.reduce((total, amount) => total + amount, 0n)
    : null;
  const exhibitLines = manifest
    .filter((document) => document.storageKey)
    .map((document, index) => `${index + 1}. ${document.name} (${document.role}; uploaded evidence)`);

  const paragraphs = [`**To:** ${metadata.recipientIdrEntity || "IDR entity"}  \n**Dispute ID:** ${metadata.disputeId || "Not provided"}`];
  if (includesPricing) {
    paragraphs.push(freeTextIntake
      ? `## IDR offer (user-entered)\n\n${idrCase.offerNarrative.trim()}\n\n## Pricing statement\n\n${lineOfferBlocks.length ? lineOfferBlocks.join("\n\n") : "No service lines could be extracted from the case EOB. Review the source before use."}`
      : `## Pricing statement\n\n${lineOfferBlocks.join("\n\n")}\n\nCase-level offer total (reference figure only): ${referenceTotal === null ? "incomplete" : formatMinorUnits(referenceTotal, idrCase.offer?.currency || "USD")}. This total does not establish that evidence for one service line supports another.`);
  }
  if (includesClinical) {
    const clinicalServiceLines = serviceLines.map((line, index) =>
      `${index + 1}. ${line.cptHcpcs}${line.modifiers ? `, modifier(s) ${line.modifiers}` : ""}${line.units ? `, ${line.units} unit(s)` : ""}; ${line.serviceScope}`
    );
    paragraphs.push(`## Services addressed\n\n${clinicalServiceLines.join("\n")}`);
    paragraphs.push(`## Patient-specific documented work\n\n${findingLines.length ? findingLines.join("\n\n") : "No patient-specific documented work events are included."}`);
    if (clinicalAnalysis) {
      const extractedFindings = (clinicalAnalysis.findings || []).flatMap((finding) => {
        if (finding.evidenceType !== "DOCUMENTED" || !finding.sourceRefs?.length) return [];
        const citations = finding.sourceRefs.map((ref) => {
          const source = validateSourceRef(ref, manifest);
          const document = manifest.find((item) => item.id === ref.documentId);
          return source.valid && source.attached && document
            ? `${document.name}${ref.page ? `, p. ${ref.page}` : ""}`
            : "";
        });
        if (citations.some((citation) => !citation)) return [];
        return [`- ${finding.finding}\n  Relevance: ${finding.relevance} [${citations.join("; ")}]`];
      });
      paragraphs.push(`## Medical-necessity evidence analysis — human review required\n\n${extractedFindings.length ? extractedFindings.join("\n\n") : "No source-linked documented findings were available for inclusion."}\n\nThese model-extracted evidence candidates require verification against the medical notes; they are not a medical-necessity determination.`);
      clinicalReviewNotes.push(
        ...(clinicalAnalysis.missingInformation || []),
        ...(clinicalAnalysis.riskFlags || [])
      );
    }
  }
  if (includesPricing && dateRange) paragraphs.push(`Service date(s): ${dateRange}${metadata.location ? `; location: ${metadata.location}` : ""}.`);
  if (metadata.providerGroup || metadata.renderingProvider || signatureName || metadata.signatureDetails) {
    paragraphs.push(`## Signature\n\n${metadata.providerGroup || ""}${metadata.renderingProvider ? `  \n${metadata.renderingProvider}` : ""}${signatureName || metadata.signatureDetails ? `  \n${signatureName || metadata.signatureDetails}` : ""}`);
  }
  if (exhibitLines.length) paragraphs.push(`## Exhibit index\n\n${exhibitLines.join("\n")}`);

  const hasTargetRemittance = manifest.some((document) =>
    document.role === "TARGET_CLAIM_REMITTANCE" &&
    document.storageKey &&
    (!document.pageAssignments?.length ||
      document.pageAssignments.some((assignment) => assignment.role === "TARGET_CLAIM_REMITTANCE"))
  );
  const reviewNotes = [
    ...(!pricingAnalysis ? ["No completed pricing assessment is available; code-to-remittance discrepancies have not been ruled out."] : []),
    ...(pricingAnalysis && !hasTargetRemittance ? ["No current remittance source is assigned to the target-claim role; code-to-remittance comparison is unavailable."] : []),
    ...serviceLines.filter((line) => !assessmentByLine.has(line.lineId))
      .map((line) => `No reviewed pricing assessment is available for ${line.cptHcpcs || line.lineId}.`),
    ...rationaleReviewNotes,
    ...clinicalReviewNotes,
    ...(!findingLines.length && includesClinical ? ["No clinical work events are included."] : []),
    "Verify every amount, code, payment basis, source citation, and statement before using this draft.",
  ];
  return {
    status: "DRAFT_REQUIRES_REVIEW",
    outputMode,
    offerMarkdown: paragraphs.join("\n\n") + "\n",
    reviewNotes,
    blockers: [],
  };
}

export function createReviewReport(idrCase, manifest, analysisStatus = null) {
  const blockers = getBlockingIssues(idrCase);
  const reconciliation = reconcilePayments(idrCase?.payments);
  const safeManifest = Array.isArray(manifest) ? manifest : [];
  const reportIssues = [
    ...blockers.map((issue) => ({ severity: "BLOCKING", ...issue })),
  ];
  if (!safeManifest.length) {
    reportIssues.push({
      severity: "REVIEW",
      code: "NO_SOURCE_DOCUMENTS",
      description: "No supporting documents are attached.",
    });
  }
  for (const document of safeManifest) {
    reportIssues.push({
      severity: "REVIEW",
      code: "DOCUMENT_NOT_PROCESSED",
      description: `${document.name}: extraction/OCR has not been run; pages and contents are not verified.`,
    });
  }
  const hashCounts = new Map();
  for (const document of safeManifest) {
    if (document.hash) hashCounts.set(document.hash, (hashCounts.get(document.hash) || 0) + 1);
  }
  for (const [hash, count] of hashCounts) {
    if (count > 1) {
      reportIssues.push({
        severity: "REVIEW",
        code: "DUPLICATE_DOCUMENT_COPY",
        description: `${count} document entries have the same SHA-256 (${hash.slice(0, 12)}…). Duplicate copies are retained as sources, not counted as independent payment evidence.`,
      });
    }
  }
  if (idrCase?.intakeMode !== "free_text" && reconciliation.status === "MISMATCH") {
    reportIssues.push({
      severity: "REVIEW",
      code: "PAYMENT_RECONCILIATION_MISMATCH",
      description: "Case-level initial payment, supplemental payment, and signed reversal do not equal the recorded allowance reference figure.",
    });
  }
  if (idrCase?.intakeMode !== "free_text" && reconciliation.status === "INCOMPLETE") {
    reportIssues.push({
      severity: "REVIEW",
      code: "PAYMENT_RECONCILIATION_INCOMPLETE",
      description: "Case-level payment reconciliation is incomplete; blank amounts remain unknown, not zero.",
    });
  }
  if (!idrCase?.research?.enabled) {
    reportIssues.push({
      severity: "INFO",
      code: "LEGAL_RESEARCH_DISABLED",
      description: "Legal research is disabled; this workspace has not verified legal authorities.",
    });
  } else {
    reportIssues.push({
      severity: "REVIEW",
      code: "LEGAL_RESEARCH_REQUIRES_REVIEW",
      description: "Enabling isolated federal legal research does not itself verify authority; any generated findings require current primary-authority verification and counsel review.",
    });
  }
  const sourceReferences = (idrCase?.clinicalFindings || []).map((finding) => ({
    category: "CLINICAL",
    sourceRef: finding.sourceRef,
    validation: validateSourceRef(finding.sourceRef, safeManifest),
  }));
  if (idrCase?.offer?.sourceRef) {
    sourceReferences.push({
      category: "OFFER",
      sourceRef: idrCase.offer.sourceRef,
      validation: validateSourceRef(idrCase.offer.sourceRef, safeManifest),
    });
  }
  if (String(idrCase?.benchmark?.amount || "").trim()) {
    const screening = screenBenchmarkEligibility(idrCase.benchmark, idrCase.offer, safeManifest);
    reportIssues.push({
      severity: "REVIEW",
      code: "BENCHMARK_COMPLETENESS_SCREEN",
      description: `${screening.status}; permitted use remains AUDIT_ONLY. ${screening.checks.map((check) => check.detail).join(" ")}`,
    });
    if (idrCase.benchmark.sourceRef) {
      sourceReferences.push({
        category: "BENCHMARK",
        sourceRef: idrCase.benchmark.sourceRef,
        validation: validateSourceRef(idrCase.benchmark.sourceRef, safeManifest),
      });
    } else {
      reportIssues.push({
        severity: "REVIEW",
        code: "BENCHMARK_SOURCE_MISSING",
        description: "Benchmark amount has no source reference and remains audit-only.",
      });
    }
    reportIssues.push({
      severity: "REVIEW",
      code: "BENCHMARK_AUDIT_ONLY",
      description: "User-entered benchmark is UNVERIFIED_CONTEXT_ONLY and AUDIT_ONLY; it is not valuation support.",
    });
  }
  for (const [paymentKey, payment] of Object.entries(idrCase?.payments || {})) {
    if (!String(payment?.amount || "").trim() && !payment?.sourceRef) continue;
    if (!payment?.sourceRef) {
      reportIssues.push({
        severity: "REVIEW",
        code: "PAYMENT_SOURCE_MISSING",
        description: `${paymentKey} has no source reference.`,
      });
      continue;
    }
    sourceReferences.push({
      category: `PAYMENT_${paymentKey.toUpperCase()}`,
      sourceRef: payment.sourceRef,
      validation: validateSourceRef(payment.sourceRef, safeManifest),
    });
  }
  for (const comparator of idrCase?.comparators || []) {
    const screening = screenComparatorEligibility(comparator, idrCase, safeManifest);
    reportIssues.push({
      severity: "REVIEW",
      code: "COMPARATOR_COMPLETENESS_SCREEN",
      description: `${comparator.comparatorId}: ${screening.status}; ${screening.checks.map((check) => check.detail).join(" ")}`,
    });
    if (!comparator.sourceRef) {
      reportIssues.push({
        severity: "REVIEW",
        code: "COMPARATOR_SOURCE_MISSING",
        description: `${comparator.comparatorId} has no source reference.`,
      });
      continue;
    }
    sourceReferences.push({
      category: "COMPARATOR",
      sourceRef: comparator.sourceRef,
      validation: validateSourceRef(comparator.sourceRef, safeManifest),
    });
  }
  const researchStatus = !idrCase?.research?.enabled
    ? "DISABLED"
    : analysisStatus?.legalResearch?.status || "ENABLED_NOT_RUN";
  return {
    status: blockers.length ? "NEEDS_INPUT" : "DRAFT_REQUIRES_REVIEW",
    issues: reportIssues,
    paymentReconciliation: {
      ...reconciliation,
      calculated: formatMinorUnits(reconciliation.calculatedMinorUnits, idrCase?.payments?.initialPayment?.currency),
      allowance: formatMinorUnits(reconciliation.allowanceMinorUnits, idrCase?.payments?.serviceLineAllowance?.currency),
    },
    sourceReferences,
    researchStatus,
    autoFiling: false,
  };
}
